
import { graphql, useStaticQuery } from 'gatsby'
import PropTypes from 'prop-types'
import * as React from 'react'
import { Helmet } from 'react-helmet'

function SEO({ description, lang, meta, title, image, pathname, article, breadcrumbs, faq }) {
	const {
		cms: { globalSeo },
		site: { siteMetadata: { siteUrl } },
	} = useStaticQuery(
		graphql`
			query {
				cms: datoCmsSite {
					globalSeo {
						siteName
						twitterAccount
						fallbackSeo {
							title
							description
							image { url }
						}
					}
				}
				site {
					siteMetadata {
						siteUrl
					}
				}
			}
		`
	)

	const metaDescription = description || globalSeo?.fallbackSeo?.description
	const pageTitle = title || globalSeo?.fallbackSeo?.title || globalSeo?.siteName

	const normalizedPath = pathname && pathname !== '/' ? (pathname.endsWith('/') ? pathname : `${pathname}/`) : '/'
	const canonicalUrl = `${siteUrl}${normalizedPath}`

	const resolveImage = src => {
		if (!src) return `${siteUrl}/share.png`
		if (src.startsWith('http')) return src
		if (src.startsWith('//')) return `https:${src}`
		return `${siteUrl}${src}`
	}
	const absoluteImage = resolveImage(image || globalSeo?.fallbackSeo?.image?.url)

	return (
		<Helmet
			htmlAttributes={{ lang }}
			title={pageTitle}
			meta={[
				{ name: `description`, content: metaDescription },
				{ property: `og:title`, content: pageTitle },
				{ property: `og:description`, content: metaDescription },
				{ property: `og:type`, content: article ? `article` : `website` },
				{ property: `og:url`, content: canonicalUrl },
				{ property: `og:site_name`, content: globalSeo?.siteName || `SIERA Property Group` },
				{ property: `og:image`, content: absoluteImage },
				{ name: `twitter:card`, content: `summary_large_image` },
				{ name: `twitter:site`, content: globalSeo?.twitterAccount || `` },
				{ name: `twitter:title`, content: pageTitle },
				{ name: `twitter:description`, content: metaDescription },
				{ name: `twitter:image`, content: absoluteImage },
			].concat(article ? [
				...(article.datePublished ? [{ property: 'article:published_time', content: article.datePublished }] : []),
				...(article.dateModified ? [{ property: 'article:modified_time', content: article.dateModified }] : []),
			] : [], meta)}
			link={[{ rel: `canonical`, href: canonicalUrl }]}
		>
			{article && <script type='application/ld+json'>{JSON.stringify({
				'@context': 'https://schema.org',
				'@graph': [
					{
						'@type': 'BlogPosting',
						'@id': `${canonicalUrl}#article`,
						mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
						url: canonicalUrl,
						description: metaDescription,
						publisher: {
							'@type': 'Organization',
							'@id': `${siteUrl}/#organization`,
							name: 'SIERA Property Group',
							url: siteUrl,
							logo: { '@type': 'ImageObject', url: `${siteUrl}/share.png` },
						},
						...article,
					},
					...(breadcrumbs?.length ? [{
						'@type': 'BreadcrumbList',
						'@id': `${canonicalUrl}#breadcrumb`,
						itemListElement: breadcrumbs.map((crumb, index) => ({
							'@type': 'ListItem',
							position: index + 1,
							name: crumb.name,
							...(crumb.item ? { item: crumb.item } : {}),
						})),
					}] : []),
					...(faq?.length ? [{
						'@type': 'FAQPage',
						'@id': `${canonicalUrl}#faq`,
						mainEntity: faq.map(({ question, answer }) => ({
							'@type': 'Question',
							name: question,
							acceptedAnswer: { '@type': 'Answer', text: answer },
						})),
					}] : []),
				],
			}).replace(/</g, '\\u003c')}</script>}
		</Helmet>
	)
}

SEO.defaultProps = {
	lang: `en`,
	meta: [],
	description: ``,
	pathname: `/`,
}

SEO.propTypes = {
	description: PropTypes.string,
	lang: PropTypes.string,
	meta: PropTypes.arrayOf(PropTypes.object),
	title: PropTypes.string.isRequired,
	pathname: PropTypes.string,
}

export default SEO
